// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import { getUnitListPrice, getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { xParserOptions } from "@steiner/XDL/Utilities/BasicSelectors";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect } from "react-redux";

type OwnProps = TypographyPresentationProps & {
    pricing: ProductPriceDto;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    currencySymbol: string;
    qtyOrdered?: number;
    // XNG-Change
    label?: string;
    unitOfMeasure?: string;
    hideRegularLabel?: boolean;
};

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductPriceSavingsMessage: FC<Props> = ({
    pricing,
    showSavingsAmount,
    showSavingsPercent,
    currencySymbol,
    qtyOrdered = 1,
    language,
    // XNG-Change
    unitOfMeasure = "",
    label = "Regular Price",
    hideRegularLabel,
    ...otherProps
}) => {
    const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered);
    const { price: unitListPrice, priceDisplay: unitListPriceDisplay } = getUnitListPrice(pricing, qtyOrdered);
    const hasPriceSavings = unitNetPrice < unitListPrice;
    if (!language || !pricing || !hasPriceSavings) {
        return null;
    }

    const savingsAmount = Math.round((unitListPrice - unitNetPrice) * 100) / 100;
    const localizedSavingsAmount = getLocalizedCurrency({ amount: savingsAmount, language, currencySymbol });
    const savingsPercent = Math.round((savingsAmount / unitListPrice) * 100);

    /**
     * XNG-Change
     * Use custom label coming from props
     * parse to apply del style
     */
    let message = "";
    if (showSavingsAmount && showSavingsPercent) {
        message = translate(
            `${label}: {0}, you save {1} ({2}%)`,
            unitListPriceDisplay,
            localizedSavingsAmount,
            `${savingsPercent}`,
        );
    } else if (showSavingsPercent) {
        message = translate(`${label}: {0}, you save {1}%`, unitListPriceDisplay, `${savingsPercent}`);
    } else if (showSavingsAmount) {
        message = translate(`${label}: {0}, you save {1}`, unitListPriceDisplay, localizedSavingsAmount);
    } else if (hideRegularLabel) {
        message = translate(`<del>{0}</del> {1}`, unitListPriceDisplay, unitOfMeasure);
    } else {
        message = translate(`${label}: <del>{0}</del> {1}`, unitListPriceDisplay, unitOfMeasure);
    }

    // XNG-Change
    // Use parse to apply del style
    return (
        <Typography {...otherProps} data-test-selector="productPriceSavingsMessage">
            {parse(message, xParserOptions)}
        </Typography>
    );
};

export default connect(mapStateToProps)(ProductPriceSavingsMessage);
